import { useState } from "react"
import { Typography } from "@mui/material"
import { enqueueSnackbar } from "notistack"
import { useNavigate } from "react-router-dom"
import { makeStyles } from "tss-react/mui"

import { ROOT_ROUTE } from "../../../constants/routes"
import { ErrorDisplay } from "../../../core/api/errorTypes"
import { useApiQuery } from "../../../core/api/useApiQuery"
import { useApiMutation } from "../../../core/api/useApiMutation"
import { LocalizedStrict } from "../../../modules/localization/components/AppLocalized"
import { useAppLocalization } from "../../../modules/localization/components/AppLocalizationProvider"
import { impersonateUser } from "../../../modules/login/loginApi"
import { login, logout } from "../../../modules/login/loginSlice"
import { isAuthenticationComplete } from "../../../modules/login/loginTypes"
import { useAppDispatch } from "../../../modules/store/storeHooks"
import { getUserDetails, type GetUserDetailsResponse, UserQuery } from "../../../modules/users/userApi"

import { ConfirmModal } from "./ConfirmModal"

export type ImpersonationTarget = Readonly<{
    userId: number
    displayName: string
    email: string
}>

type ImpersonationTargetSource = Readonly<{
    id: number
    displayName: string
    email: string
}>

export function toImpersonationTarget({ id, displayName, email }: ImpersonationTargetSource): ImpersonationTarget {
    return { userId: id, displayName, email }
}

type ImpersonationConfirmDialogProps = Readonly<{
    target: ImpersonationTarget | null
    onClose: () => void
}>

const useStyles = makeStyles()((theme) => ({
    identity: {
        fontSize: theme.typography.body2.fontSize,
        lineHeight: theme.typography.body2.lineHeight,
        fontWeight: theme.typography.fontWeightRegular,
        color: theme.palette.tailwind.slate[400],
    },
    message: {
        textAlign: "center",
        color: theme.palette.tailwind.slate[500],
    },
}))

const selectUser = (data: GetUserDetailsResponse) => data.user

/**
 * Confirmation shown before a supporter signs in as the picked user. On confirmation the
 * session is swapped the way the login flow does it, with logout first so every slice
 * listening for it resets. The switch is deliberately one-way: the way back is logging out.
 */
export function ImpersonationConfirmDialog({ target, onClose }: ImpersonationConfirmDialogProps) {
    const { classes } = useStyles()
    const dispatch = useAppDispatch()
    const navigate = useNavigate()
    const { l10n } = useAppLocalization()

    const [isImpersonating, setImpersonating] = useState(false)

    const { mutateAsync: callImpersonateUser } = useApiMutation(impersonateUser, {})

    // The list endpoints lack the username and swap givenName/surname; the details endpoint has both right
    const userDetails = useApiQuery(getUserDetails, {
        queryName: UserQuery.USER_DETAILS,
        dependencies: { userId: target?.userId ?? 0 },
        enabled: Boolean(target),
        errorDisplay: ErrorDisplay.NONE,
        select: selectUser,
    })

    const user = userDetails.data
    const fullName = user?.displayName || target?.displayName || ""
    const firstName = user?.givenName || fullName
    const identityLine = [...new Set([user?.username, user?.email ?? target?.email].filter(Boolean))].join(" · ")
    const isOpen = Boolean(target) && (userDetails.isSuccess || userDetails.isError)

    const handleConfirm = async () => {
        if (!target || !user?.username) return

        setImpersonating(true)
        try {
            const response = await callImpersonateUser({ userId: target.userId, username: user.username })

            if (!isAuthenticationComplete(response)) {
                enqueueSnackbar({
                    variant: "error",
                    message: l10n.getString("auth-flow-authentication-unexpected-server-error", {
                        action: response.authenticationResult.action,
                    }),
                })
                return
            }

            if (response.authenticationResult.message) {
                enqueueSnackbar({ variant: "success", message: response.authenticationResult.message })
            }

            // Both dispatches land in one batch, so the auth guard never sees the gap
            dispatch(logout())
            dispatch(
                login({
                    authToken: response.authentication.token,
                    user: response.user,
                }),
            )
            navigate(ROOT_ROUTE)
        } catch {
            // The API layer already showed the error snackbar; the dialog stays open
        } finally {
            setImpersonating(false)
        }
    }

    return (
        <LocalizedStrict
            id="impersonation-confirm-dialog"
            attrs={{ title: true, buttonLabelSubmit: true, buttonLabelCancel: true }}
            vars={{ name: fullName, firstName }}
        >
            <ConfirmModal
                open={isOpen}
                onClose={onClose}
                title={`Log in as ${fullName}?`}
                message={<span className={classes.identity}>{identityLine}</span>}
                buttonLabelCancel="Cancel"
                buttonLabelSubmit={`Log in as ${firstName}`}
                buttonColorSubmit="black"
                buttonSubmitDisabled={!user?.username}
                buttonSubmitLoading={isImpersonating}
                autoCloseAfterSubmit={false}
                handleClick={handleConfirm}
            >
                <LocalizedStrict id="impersonation-confirm-dialog-message" vars={{ firstName }}>
                    <Typography variant="body1" className={classes.message}>
                        You'll see the portal exactly as {firstName} sees it. Everything you do is recorded in{" "}
                        {firstName}'s history under your own name.
                    </Typography>
                </LocalizedStrict>
            </ConfirmModal>
        </LocalizedStrict>
    )
}
